import React, { useState, useEffect, useRef, useMemo } from 'react';
import expFilesList from '../Data/expFiles.json';

const expFilesSet = new Set(expFilesList);

function CardsPage({ data }) {
  // const last5 = location.href.slice(-5);
  // const isQaida = data[0]?.letter !== null;
  const isQaida = window.location.href.slice(-5).toLowerCase() === "qaida";
  const [popupSequence, setPopupSequence] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [audioEnded, setAudioEnded] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [explanationEnded, setExplanationEnded] = useState(false);

  const popupRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const expAudioRef = useRef(null);

  // Picked once per card so the thumbnail and its reflection always show the
  // same photo, and so it doesn't re-randomize (and flicker) on every render.
  const cardImageIndices = useMemo(
    () => data.map(() => Math.floor(Math.random() * 6) + 1),
    [data]
  );

  const handleThumbnailClick = (group) => {
    if (!group.words?.length) return;
    setPopupSequence(group);
    setCurrentIndex(0);
    setIsVideoPlaying(false);
    setShowConfirmDialog(false);
  };

  const handleNext = () => {
    if (!popupSequence) return;
    const nextIndex = currentIndex + 1;

    if (nextIndex >= popupSequence.words.length) {
      const nextGroupIndex = data.findIndex(d => d === popupSequence) + 1;
      if (nextGroupIndex < data.length) {
        setShowConfirmDialog(true);
      } else {
        closePopup();
      }
    } else {
      setCurrentIndex(nextIndex);
      setIsVideoPlaying(false);
    }
  };

  const confirmAdvance = () => {
    const nextGroupIndex = data.findIndex(d => d === popupSequence) + 1;
    if (nextGroupIndex < data.length) {
      setShowConfirmDialog(false);
      setTimeout(() => handleThumbnailClick(data[nextGroupIndex]), 100);
      setSecondsLeft(5);
    } else {
      closePopup();
    }
  };

  const closePopup = () => {
    setPopupSequence(null);
    setCurrentIndex(0);
    setIsVideoPlaying(false);
    setShowConfirmDialog(false);
    setAudioEnded(false);
    setVideoEnded(false);
    setExplanationEnded(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (expAudioRef.current) {
      expAudioRef.current.pause();
      expAudioRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closePopup();
    if (e.key === 'ArrowLeft') handleNext();
    if (e.key === 'ArrowRight' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsVideoPlaying(false);
    }
  };

  useEffect(() => {
    if (popupSequence && popupRef.current) {
      popupRef.current.focus();
      const word = popupSequence.words[currentIndex];

      setAudioEnded(false);
      setVideoEnded(!word.videoFile);
      setExplanationEnded(false);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const wordAudio = new Audio(`${process.env.PUBLIC_URL}/sounds/` + word.soundFile);
      audioRef.current = wordAudio;

      if (isQaida && popupSequence.letter) {
        const letterAudio = new Audio(`${process.env.PUBLIC_URL}/sounds/${popupSequence.letter}_00.mp3`);
        let wordAudioStarted = false;
        const startWordAudio = () => {
          if (wordAudioStarted) return;
          wordAudioStarted = true;
          wordAudio.play();
        };
        letterAudio.play();
        letterAudio.onended = startWordAudio;
        letterAudio.onerror = startWordAudio;
      } else {
        wordAudio.play();
      }

      wordAudio.onended = () => setAudioEnded(true);

      if (word.videoFile) {
        setIsVideoPlaying(true);
      }
    }
  }, [popupSequence, currentIndex]);

  useEffect(() => {
    if (!audioEnded) return;

    const currentWord = popupSequence?.words[currentIndex];
    const expFile = currentWord?.soundFile?.replace(/\.mp3$/i, '_exp.mp3');
    if (expFile && expFilesSet.has(expFile)) {
      const expAudio = new Audio(`${process.env.PUBLIC_URL}/sounds/${expFile}`);
      expAudioRef.current = expAudio;
      const done = () => setExplanationEnded(true);
      expAudio.onended = done;
      expAudio.onerror = done;
      expAudio.play();
    } else {
      setExplanationEnded(true);
    }
  }, [audioEnded]);

  useEffect(() => {
    if (!(explanationEnded && videoEnded)) return;
    const timerId = setTimeout(() => handleNext(), 1500);
    return () => clearTimeout(timerId);
  }, [explanationEnded, videoEnded]);

  const word = popupSequence?.words[currentIndex];
  const nextWord = popupSequence?.words[currentIndex + 1];
const [secondsLeft, setSecondsLeft] = useState(5);


useEffect(() => {
  if (showConfirmDialog) setSecondsLeft(5);
}, [showConfirmDialog]);

useEffect(() => {
  if (!showConfirmDialog || secondsLeft <= 0) return;

  const timerId = setTimeout(() => {
    setSecondsLeft(prev => prev - 1);
  }, 1000);

  return () => clearTimeout(timerId);
}, [showConfirmDialog, secondsLeft]);

useEffect(() => {
  if (showConfirmDialog && secondsLeft === 0) {
    confirmAdvance();
  }
}, [showConfirmDialog, secondsLeft, confirmAdvance]);


  const overlayStyles = {
    mediaContainer: {
      position: 'relative',
      marginBottom: '20px',
      borderRadius: '12px',
      height: '90vh',
    },
    wordBadge: {
      position: 'absolute',
      top: '0vh',
      right: '0vw',
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      color: '#fff',
      padding: '5px 10px',
      borderRadius: '999px',
      fontSize: '32px',
      fontWeight: '700',
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      zIndex: 2,
      maxWidth: '70%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'none',
    },
    letterBadge: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      color: '#333',
      padding: '6px 14px',
      borderRadius: '999px',
      fontSize: '22px',
      fontWeight: '600',
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      zIndex: 2,
      pointerEvents: 'none',
    },
    nextButton: {
      position: 'absolute',
      top: '5vh',
      left: '1vh',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      color: '#888',
      padding: '2px 4px',
      borderRadius: '999px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
      zIndex: 2,
      maxWidth: '55%',
      whiteSpace: 'nowrap',
    },
    nextButtonText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }
  };

  const handleVideoError = () => {
    setIsVideoPlaying(false);
    setVideoEnded(true);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (err) {
        // no-op if pause fails
      }
      videoRef.current = null;
    }
  };

  return (
    <div className="page-container">
      <div className="items-grid">
        {data.map((item, idx) => {
          const imgSrc = `${process.env.PUBLIC_URL}/images/${(item.letter || item.imageFile || 'default')}_${String(cardImageIndices[idx]).padStart(2, '0')}.jpg`;
          return (
            <div key={idx} className="item-card" onClick={() => handleThumbnailClick(item)}>
              <div className="item-card-image-wrap">
                <img src={imgSrc} alt={item.harf || item.title} className="item-card-img" />
                <img src={imgSrc} alt="" aria-hidden="true" className="item-card-reflection" />
              </div>
              <div className="harf-badge">{item.harf || item.title}</div>
            </div>
          );
        })}
      </div>

      {popupSequence && word && (
        <div
          ref={popupRef}
          tabIndex="0"
          onClick={closePopup}
          onKeyDown={handleKeyDown}
          className="overlay cardspage-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="cardspage-modal"
          >
            <div style={overlayStyles.mediaContainer}>
              {isVideoPlaying && word.videoFile && isQaida ? (
                <>
                  <video
                    ref={videoRef}
                    muted
                    autoPlay
                    controls
                    onEnded={() => setVideoEnded(true)}
                    onError={handleVideoError}

                    src={`${process.env.PUBLIC_URL}/videos/${word.videoFile}`}
                    className="cardspage-video"
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/images/${word.imageFile}`}
                    alt=""
                    className="cardspage-video-overlay-img"
                  />
                </>
              ) : (
                <img
                  src={`${process.env.PUBLIC_URL}/images/${word.imageFile}`}
                  alt={word.word}
                  className="cardspage-image"
                />
              )}

              <div style={overlayStyles.wordBadge}>{popupSequence.harf}⬅️{word.word}</div>

              {/* {popupSequence.harf && (
                <div style={overlayStyles.letterBadge}>{popupSequence.harf}</div>
              )} */}

              {nextWord && (
                <button
                  type="button"
                  style={overlayStyles.nextButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                >
                  <span style={overlayStyles.nextButtonText}>اگلا لفظ: {nextWord.word}</span>
                  <span role="img" aria-label="Next word">⬅️</span>
                </button>
              )}
            </div>

          </div>

          {/* Confirm dialog */}
          {showConfirmDialog && (
            <div className="cardspage-confirm-dialog">
              <p className="cardspage-confirm-text">تمام الفاظ مکمل ہوگئے۔ کیا اگلے حرف پر چلیں؟</p>
              <button onClick={confirmAdvance} className="cardspage-confirm-btn cardspage-confirm-btn--yes">
                {secondsLeft > 0 ? `${secondsLeft}` : ''}
              </button>
              <button onClick={closePopup} className="cardspage-confirm-btn cardspage-confirm-btn--no">نہیں</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CardsPage;
